<template>
  <a-spin :spinning="loading">
    <div class="dashboard-container">
      <a-breadcrumb style="margin: 16px 0px;">
        <a-breadcrumb-item>产品开发者</a-breadcrumb-item>
        <a-breadcrumb-item>概览</a-breadcrumb-item>
        <a-breadcrumb-item>产品开发者数量</a-breadcrumb-item>
      </a-breadcrumb>
      <div class="overview-highlight-box">
        <span class="caption-and-select-container">
          <span class="caption">时间维度</span>
          <a-select v-model:value="year" class="select">
            <a-select-option v-for="opt in yearOptions" :key="opt.key" :value="opt.value">
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </span>
        <!-- 顶部统计卡片 -->
        <!-- 顶部核心统计区 -->
        <div class="top-stats-card">
          <div class="stats-item">
            <div class="stats-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                <path
                  d="M12 6.5c-2.49 0-4.5 2.01-4.5 4.5s2.01 4.5 4.5 4.5 4.5-2.01 4.5-4.5-2.01-4.5-4.5-4.5zm0 7c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </div>
            <div class="stats-content">
              <div class="stats-label">产品开发者数量</div>
              <div class="stats-value">{{ formatNumber(yearlyStats.totals.developerCount) }}<span
                  class="stats-unit">人</span>
              </div>
            </div>
          </div>

          <div class="stats-desc">
            数据说明：按产品数量 × 每款产品预估开发工作量，预估投入产品开发工作量 {{ formatNumber(yearlyStats.totals.totalWorkload) }} 人月，按每个开发者平均投入
            6 人月计算，预估开发者人数为 {{ formatNumber(yearlyStats.totals.developerCount) }} 人。
          </div>
        </div>

        <!-- 二级统计区 -->
        <div class="secondary-stats">
          <div class="secondary-item">
            <span>通过兼容性测评产品</span>
            <span class="highlight">{{ formatNumber(yearlyStats.totals.totalProductCount) }}<span>款</span></span>
            (非华为)
          </div>
          <div class="secondary-item">
            <span>预估适配开发工作量</span>
            <span class="highlight">{{ formatNumber(yearlyStats.totals.totalWorkload) }}<span>人月</span></span>
          </div>
        </div>

        <!-- 产品分类卡片区 -->
        <div class="product-cards-container">
          <!-- 商用设备 -->
          <el-card class="product-card commercial-card">
            <template #header>
              <span class="card-title">商用设备</span>
            </template>
            <div class="card-main-row">
              <div class="main-item">
                <span class="item-label">产品数量</span>
                <span class="item-value">{{ formatNumber(yearlyStats.types['商用设备'].productTotal) }}<span>款</span></span>
              </div>
              <div class="main-item">
                <span class="item-label">开发人力</span>
                <span class="item-value">{{ formatNumber(yearlyStats.types['商用设备'].workload) }}<span>人月</span></span>
              </div>
            </div>
            <div class="card-sub-row">
              <div class="sub-item">
                <span>标准系统 {{ formatNumber(yearlyStats.types['商用设备'].standardCount) }} 款</span>
                <span class="sub-desc">单款投入：30 人月</span>
              </div>
              <div class="sub-item">
                <span>小型系统 {{ formatNumber(yearlyStats.types['商用设备'].smallCount) }} 款</span>
                <span class="sub-desc">单款投入：15 人月</span>
              </div>
              <div class="sub-item">
                <span>轻量系统 {{ formatNumber(yearlyStats.types['商用设备'].lightCount) }} 款</span>
                <span class="sub-desc">单款投入：5 人月</span>
              </div>
            </div>
          </el-card>

          <!-- 模组/开发板 -->
          <el-card class="product-card module-card">
            <template #header>
              <span class="card-title">模组/开发板</span>
            </template>
            <div class="card-main-row">
              <div class="main-item">
                <span class="item-label">产品数量</span>
                <span class="item-value">{{ formatNumber(yearlyStats.types['模组/开发板'].productTotal)
                }}<span>款</span></span>
              </div>
              <div class="main-item">
                <span class="item-label">开发人力</span>
                <span class="item-value">{{ formatNumber(yearlyStats.types['模组/开发板'].workload) }}<span>人月</span></span>
              </div>
            </div>
            <div class="card-sub-row">
              <div class="sub-item">
                <span>标准系统 {{ formatNumber(yearlyStats.types['模组/开发板'].standardCount) }} 款</span>
                <span class="sub-desc">单款投入：15 人月</span>
              </div>
              <div class="sub-item">
                <span>小型系统 {{ formatNumber(yearlyStats.types['模组/开发板'].smallCount) }} 款</span>
                <span class="sub-desc">单款投入：10 人月</span>
              </div>
              <div class="sub-item">
                <span>轻量系统 {{ formatNumber(yearlyStats.types['模组/开发板'].lightCount) }} 款</span>
                <span class="sub-desc">单款投入：3 人月</span>
              </div>
            </div>
          </el-card>

          <!-- 发行版 -->
          <el-card class="product-card release-card">
            <template #header>
              <span class="card-title">发行版</span>
            </template>
            <div class="card-main-row">
              <div class="main-item">
                <span class="item-label">产品数量</span>
                <span class="item-value">{{ formatNumber(yearlyStats.types['发行版'].productTotal) }}<span>款</span></span>
              </div>
              <div class="main-item">
                <span class="item-label">开发人力</span>
                <span class="item-value">{{ formatNumber(yearlyStats.types['发行版'].workload) }}<span>人月</span></span>
              </div>
            </div>
            <div class="card-sub-row">
              <div class="sub-item">
                <span>标准系统 {{ formatNumber(yearlyStats.types['发行版'].standardCount) }} 款</span>
                <span class="sub-desc">单款投入：30 人月</span>
              </div>
              <div class="sub-item">
                <span>小型系统 {{ formatNumber(yearlyStats.types['发行版'].smallCount) }} 款</span>
                <span class="sub-desc">单款投入：20 人月</span>
              </div>
              <div class="sub-item">
                <span>轻量系统 {{ formatNumber(yearlyStats.types['发行版'].lightCount) }} 款</span>
                <span class="sub-desc">单款投入：20 人月</span>
              </div>
            </div>
          </el-card>
        </div>
      </div>

      <!-- 图表行：新增趋势 -->
      <div class="charts-row">
        <div class="chart-item">
          <div class="chart-title">兼容性测评产品新增趋势</div>
          <my-line-chart :data="leftChartData" :colors="['#F59E0B']" />
        </div>
        <div class="chart-item">
          <div class="chart-title">各测评类型产品数</div>
          <my-line-chart :data="rightChartData" :colors="['#2D8CF0', '#00B42A', '#9254DE']" />
        </div>
      </div>
    </div>
  </a-spin>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import { ElCard } from 'element-plus';
import { notification } from 'ant-design-vue';
import MyLineChart from '../components/MyLineChart.vue';

const years = Array.from({ length: 2026 - 2021 + 1 }, (_, i) => 2021 + i);
const maxYear = Math.max(...years);
const yearOptions = [
  { key: 'all', label: '全部', value: null },
  ...years.map((y) => ({ key: y, label: String(y), value: y }))
];

// 默认选“全部”
const year = ref(null);

const productTypes = ['商用设备', '模组/开发板', '发行版'];
const createEmptyTypeStats = () => ({
  standardCount: 0,
  smallCount: 0,
  lightCount: 0,
  productTotal: 0,
  workload: 0
});
const createEmptyStats = () => ({
  totals: {
    totalProductCount: 0,
    totalWorkload: 0,
    developerCount: 0
  },
  types: {
    '商用设备': createEmptyTypeStats(),
    '模组/开发板': createEmptyTypeStats(),
    '发行版': createEmptyTypeStats()
  }
});

const createEmptyYearStats = (yearValue) => ({
  year: yearValue,
  ...createEmptyStats()
});

const yearlyStats = ref(createEmptyStats());
const yearlyStatsList = ref(years.map((yearValue) => createEmptyYearStats(yearValue)));
const yearlyTotalList = ref([]);
const deviceStatsList = ref([]);
const loading = ref(false);

// 聚合“全部年份”时，按字段把每年类型统计累加到总对象。
const sumTypeFields = (target, source) => {
  Object.keys(target).forEach((key) => {
    target[key] += Number(source[key]);
  });
};

// 基于 getDeviceStats 的类型明细，统一回算年度总计：
// - totalProductCount（通过兼容性测评产品）= 各类型 productTotal 之和
// - totalWorkload（预估适配开发工作量）= 各类型 workload 之和
// - developerCount（产品开发者数量）= totalWorkload / 6（四舍五入）
const finalizeYearTotals = (yearStat) => {
  const typeValues = Object.values(yearStat.types);
  const summedProductCount = typeValues.reduce((sum, item) => sum + Number(item.productTotal), 0);
  const summedWorkload = typeValues.reduce((sum, item) => sum + Number(item.workload), 0);

  yearStat.totals.totalProductCount = summedProductCount;
  yearStat.totals.totalWorkload = summedWorkload;
  yearStat.totals.developerCount = Math.round(Number(yearStat.totals.totalWorkload) / 6);
};

const rebuildYearlyStatsList = () => {
  // 上半区（统计卡片 + 分类卡片）只依赖 getDeviceStats，
  // 因此先为每个展示年份创建空壳，再把设备统计覆盖进去。
  const yearMap = new Map(years.map((yearValue) => [yearValue, createEmptyYearStats(yearValue)]));

  deviceStatsList.value.forEach((item) => {
    const yearKey = Number(item.year);
    const target = yearMap.get(yearKey);
    if (!productTypes.includes(item.productType)) return;

    target.types[item.productType] = {
      standardCount: Number(item.standardCount),
      smallCount: Number(item.smallCount),
      lightCount: Number(item.lightCount),
      productTotal: Number(item.productTotal),
      workload: Number(item.workload)
    };
  });

  yearMap.forEach((value) => {
    finalizeYearTotals(value);
  });

  yearlyStatsList.value = Array.from(yearMap.values()).sort((a, b) => a.year - b.year);
};

const fetchYearlyTotal = async () => {
  try {
    // 折线图专用数据源：接口直接返回 [{ year, productType, productCount }, ...]
    const res = await axios.get('/metric/getYearlyTotal');
    yearlyTotalList.value = res.data;
  } catch (error) {
    console.error('获取年度汇总数据失败：', error);
    notification.error({
      message: '获取年度汇总数据失败',
      description: error?.message || '请稍后再试'
    });
    yearlyTotalList.value = [];
  }
};

const fetchDeviceStats = async () => {
  try {
    // 上半区专用数据源：接口包装在 { data: [...] } 中
    const res = await axios.get('/metric/getDeviceStats');
    const body = res.data;
    const payload = body.data;
    deviceStatsList.value = payload;
    rebuildYearlyStatsList();
  } catch (error) {
    console.error('获取年度设备数据失败：', error);
    notification.error({
      message: '获取年度设备数据失败',
      description: error?.message || '请稍后再试'
    });
    deviceStatsList.value = [];
    rebuildYearlyStatsList();
  }
};

// 时间维度选择“全部”时，前端加和所有年度数据
const syncYearlyStats = () => {
  if (year.value == null) {
    // “全部”走前端聚合；“单年”直接取当年对象。
    const sumStats = createEmptyStats();
    yearlyStatsList.value.forEach((item) => {
      sumStats.totals.totalProductCount += Number(item.totals.totalProductCount);
      sumStats.totals.totalWorkload += Number(item.totals.totalWorkload);
      sumStats.totals.developerCount += Number(item.totals.developerCount);
      productTypes.forEach((type) => {
        sumTypeFields(sumStats.types[type], item.types[type]);
      });
    });
    yearlyStats.value = sumStats;
    return;
  }
  const targetYear = year.value ?? maxYear;
  const found = yearlyStatsList.value.find((item) => Number(item.year) === Number(targetYear));
  yearlyStats.value = found;
};

const formatNumber = (value) => {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  return safeValue.toLocaleString('zh-CN');
};

onMounted(async () => {
  loading.value = true;
  try {
    await Promise.all([
      fetchYearlyTotal(),
      fetchDeviceStats()
    ]);
    syncYearlyStats();
  } finally {
    loading.value = false;
  }
});

watch(
  () => year.value,
  () => {
    syncYearlyStats();
  }
);


const chartYears = computed(() => years.map((y) => String(y)));

const leftChartData = computed(() => {
  // 左图：按年份聚合 getYearlyTotal 的全部产品类型数量。
  const values = years.map((y) => {
    return yearlyTotalList.value
      .filter((item) => Number(item.year) === Number(y))
      .reduce((sum, item) => sum + Number(item.productCount), 0);
  });
  return {
    years: chartYears.value,
    series: [
      { name: '兼容性测评产品数', values }
    ]
  };
});

const rightChartData = computed(() => {
  // 右图：按年份 + 产品类型聚合 getYearlyTotal。
  const commercialValues = years.map((y) => {
    return yearlyTotalList.value
      .filter((item) => Number(item.year) === Number(y) && item.productType === '商用设备')
      .reduce((sum, item) => sum + Number(item.productCount), 0);
  });
  const moduleValues = years.map((y) => {
    return yearlyTotalList.value
      .filter((item) => Number(item.year) === Number(y) && item.productType === '模组/开发板')
      .reduce((sum, item) => sum + Number(item.productCount), 0);
  });
  const releaseValues = years.map((y) => {
    return yearlyTotalList.value
      .filter((item) => Number(item.year) === Number(y) && item.productType === '发行版')
      .reduce((sum, item) => sum + Number(item.productCount), 0);
  });
  return {
    years: chartYears.value,
    series: [
      { name: '商用设备', values: commercialValues },
      { name: '模组/开发板', values: moduleValues },
      { name: '发行版', values: releaseValues }
    ]
  };
});
</script>

<style scoped>
.dashboard-container {
  /* display: flex; */
  width: 100%;
  margin-bottom: 64px;
  /* justify-content: flex-start; */
  align-items: center;
}

.overview-highlight-box {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 24px 24px 16px;
  margin-bottom: 20px;
}

.caption-and-select-container {
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 16px;
}

.caption {
  margin-right: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}

.select {
  width: 120px;
}

.dashboard-wrapper {
  padding: 20px 24px;
  background: #f5f7fa;
  min-height: 100vh;
}

/* 页面标题 */
.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  font-size: 18px;
  font-weight: 600;
  color: #1d2129;
  margin: 0;
}

/* 顶部核心统计卡片 */
.top-stats-card {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 6px;
  gap: 20px;
}

.stats-item {
  display: flex;
  /* 开启Flex布局 */
  align-items: center;
  /* 垂直居中对齐（关键，让图标和文字居中） */
  gap: 12px;
  /* 图标和内容之间的间距，可选 */
  padding: 16px;
  /* 可选：添加内边距，优化视觉 */
  background: #f9fafb;
  /* 可选：背景色，区分模块 */
  border-radius: 8px;
  /* 可选：圆角，美化 */
}

/* 图标样式：固定大小，避免变形 */
.stats-icon {
  flex-shrink: 0;
  /* 图标不被压缩 */
  color: #1890ff;
  /* 可选：设置图标颜色 */
  width: 24px;
  height: 24px;
}

/* 内容区域：占满剩余空间 */
.stats-content {
  flex: 1;
  /* 让内容区域占据剩余宽度 */
}

/* 保留原有文字样式（可根据需求调整） */
.stats-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  /* 标签和数值之间的间距 */
}

.stats-value {
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.stats-unit {
  font-size: 14px;
  color: #666;
  font-weight: normal;
  margin-left: 4px;
}

.stats-desc {
  font-size: 12px;
  color: #86909c;
  flex: 1;
  line-height: 1.5;
}

/* 二级统计区 */
.secondary-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
}

.secondary-item {
  font-size: 14px;
  color: #4e5969;
  display: flex;
  align-items: center;
  gap: 8px;
}

.secondary-item .highlight {
  font-size: 16px;
  font-weight: 600;
  color: #1d2129;
}

.secondary-item .highlight span {
  font-size: 12px;
  color: #86909c;
  margin-left: 2px;
}

/* 产品卡片容器 */
.product-cards-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 28px;
}

/* 通用卡片样式 */
.product-card {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.product-card :deep(.el-card__header) {
  padding: 12px 20px;
  border-bottom: 1px solid #ebeef5;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
}

.product-card :deep(.el-card__body) {
  padding: 20px;
}

/* 商用设备卡片 */
.commercial-card {
  border-left: 4px solid #2d8cf0;
}

/* 模组/开发板卡片 */
.module-card {
  border-left: 4px solid #00b42a;
}

/* 发行版卡片 */
.release-card {
  border-left: 4px solid #9254de;
}

/* 卡片主行 */
.card-main-row {
  display: flex;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 16px;
}

.main-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-label {
  font-size: 13px;
  color: #86909c;
}

.item-value {
  font-size: 20px;
  font-weight: 600;
  color: #1d2129;
  display: flex;
  align-items: baseline;
}

.item-value span {
  font-size: 14px;
  color: #86909c;
  margin-left: 2px;
}

/* 卡片子行 */
.card-sub-row {
  display: flex;
  justify-content: space-between;
}

.sub-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
  color: #4e5969;
}

.sub-desc {
  font-size: 12px;
  color: #86909c;
}

/* 潜在捐赠伙伴区域 */
.charts-row {
  display: flex;
  gap: 16px;
  margin: 24px 0;
}

.chart-item {
  flex: 1;
  background: #ffffff;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  color: #1d2129;
  margin-bottom: 8px;
}
</style>